import { useSnackbar } from 'notistack';
import { useMutation, useQueryClient } from 'react-query';
import { Dialog, DialogContent, DialogTitle, Grid } from '@mui/material';
import { ProductSubscription, SubscriptionPaymentTerm } from '../../shared/models';
import { FormikUtils, NumberUtils } from '../../shared/utils';
import { useApi } from '../../shared/context';
import { EmptyGuid } from '../../shared/constants';
import { CerebroApiEndpoints } from '../../shared/utils/api';
import { QueryKeys } from '../../shared/hooks';
import { Field, Formik, FormikErrors } from 'formik';
import {
	CerebroForm,
	CerebroLabel,
	CerebroMaskedInput,
	CerebroPrimaryButton,
	CerebroSecondaryButton,
} from '../../shared/components';
import { MaxCurrencyLength } from '../../shared/utils/platform';
import { getApiResponseData } from '../../shared/utils/axios';

interface SubscriptionPaymentTermFormDialogProps {
	dealGuid: string | undefined;
	companyGuid: string | undefined;
	subscription: ProductSubscription | null;
	term: SubscriptionPaymentTerm | null;
	close: () => void;
}

export const SubscriptionPaymentTermFormDialog: React.FC<SubscriptionPaymentTermFormDialogProps> = ({
	dealGuid,
	companyGuid,
	subscription,
	term,
	close,
}) => {
	const api = useApi();
	const snackbar = useSnackbar();
	const client = useQueryClient();

	const tierMutation = useMutation(
		async ({ values }: PaymentTermFormMutationVariables) => {
			const patchValues = {
				amount: NumberUtils.ParseDecimal(values.amount, undefined),
				numberOfDays: NumberUtils.ParseDecimal(values.numberOfDays, undefined),
			};

			const { data, error } = await getApiResponseData<ProductSubscription>(
				term?.guid == EmptyGuid
					? api.post(CerebroApiEndpoints.Deals.PaymentTerms(dealGuid, subscription?.guid), patchValues)
					: api.patch(
							CerebroApiEndpoints.Deals.SpecificPaymentTerm(dealGuid, subscription?.guid, term?.guid),
							patchValues
					  )
			);

			if (error) {
				throw new Error(error.response?.data);
			}

			return data;
		},
		{
			onSuccess: subscription => {
				client.invalidateQueries(QueryKeys.Deal.UpdateStatus(dealGuid));
				client.invalidateQueries(QueryKeys.Deal.PaymentTerms(dealGuid, subscription?.guid));
				snackbar.enqueueSnackbar('Subscription payment term was successfully saved.', {
					variant: 'success',
				});
				close();
			},
			onError: (error: Error) => {
				snackbar.enqueueSnackbar(error.message, {
					variant: 'error',
				});
				close();
			},
		}
	);

	var validateForm = (values: PaymentTermFormValues): PaymentTermFormErrors => {
		const errors: PaymentTermFormErrors = {};

		return errors;
	};

	return (
		<Dialog open={term !== null} onClose={close}>
			<DialogTitle>Payment Term</DialogTitle>
			<DialogContent>
				<Formik
					initialValues={{
						amount: NumberUtils.FormatDecimal(term?.amount),
						numberOfDays: NumberUtils.FormatDecimal(term?.numberOfDays),
					}}
					validate={validateForm}
					onSubmit={FormikUtils.MutateOnSubmit(tierMutation.mutate)}
				>
					{({ isSubmitting, handleSubmit }) => (
						<CerebroForm onSubmit={handleSubmit}>
							<Grid item xs={11}>
								<CerebroLabel id="amount">Amount</CerebroLabel>
								<Field
									formatas="currency"
									as={CerebroMaskedInput}
									maxLength={MaxCurrencyLength}
									id="amount"
									name="amount"
									disabled={isSubmitting}
								/>
							</Grid>
							<Grid item xs={11}>
								<CerebroLabel required id="numberOfDays">
									Number of Days
								</CerebroLabel>
								<Field
									as={CerebroMaskedInput}
									id="numberOfDays"
									name="numberOfDays"
									disabled={isSubmitting}
								/>
							</Grid>

							<CerebroPrimaryButton type="submit" loading={isSubmitting} sx={{ marginTop: 6 }}>
								Save
							</CerebroPrimaryButton>
							<CerebroSecondaryButton sx={{ marginTop: 6, marginLeft: 1 }} onClick={() => close()}>
								Cancel
							</CerebroSecondaryButton>
						</CerebroForm>
					)}
				</Formik>
			</DialogContent>
		</Dialog>
	);
};

type PaymentTermFormErrors = FormikErrors<PaymentTermFormValues>;
type PaymentTermFormMutationVariables = FormikUtils.MutateOnSubmitVariables<PaymentTermFormValues>;
interface PaymentTermFormValues {
	amount: string;
	numberOfDays: string;
}
